// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Color } from '../../../Graphics';
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
export class MACD extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'Moving Average/Convergence Divergence';
        this.Comments =
            'A trend-following momentum indicator that shows the relationship between two moving averages of prices';
        this.SetIndicatorLine('MACD', Color.DodgerBlue, 1, LineStyle.SimpleChart);
        this.SetIndicatorLine('Signal', Color.Red, 1, LineStyle.SimpleChart);
        this.SetIndicatorLine('Histogram', Color.Green, 3, LineStyle.HistogrammChart);
        this.SeparateWindow = true;
        this.FastEMA = 12;
        super.InputParameter(new InputParameterInteger('FastEMA', 'Period of FastEMA', 0, 1, 9999));
        this.SlowEMA = 26;
        super.InputParameter(new InputParameterInteger('SlowEMA', 'Period of SlowEMA', 1, 1, 9999));
        this.SignalPeriod = 9;
        super.InputParameter(new InputParameterInteger('SignalPeriod', 'Period of Signal', 2, 1, 9999));
        this.fastEMAValue = undefined;
        this.slowEMAValue = undefined;
        this.macdHistory = [];
    }
    Init() {
        super.IndicatorShortName(this.GetIndicatorShortName());
        this.alphaFast = 2 / (this.FastEMA + 1);
        this.alphaSlow = 2 / (this.SlowEMA + 1);
    }
    GetIndicatorShortName() {
        return 'MACD(' + this.FastEMA + ',' + this.SlowEMA + ',' + this.SignalPeriod + ')';
    }
    getCustomName() {
        return 'MACD (' + this.ProjectName + ')';
    }
    UpdateMACD(saveState, startIndex = 0, updateLines = true) {
        if (this.FastEMA + startIndex > this.CurrentData.Count) {
            return;
        }
        const currentPrice = this.CurrentData.getPriceByType(PriceType.Close, startIndex);
        const prevPrice = this.CurrentData.getPriceByType(PriceType.Close, startIndex + 1);
        let fast;
        if (this.fastEMAValue === undefined) {
            fast = this.alphaFast * currentPrice + (1 - this.alphaFast) * prevPrice;
        }
        else {
            fast = this.alphaFast * currentPrice + (1 - this.alphaFast) * this.fastEMAValue;
        }
        if (this.SlowEMA + startIndex > this.CurrentData.Count) {
            if (saveState) {
                this.fastEMAValue = fast;
            }
            return;
        }
        let slow;
        if (this.slowEMAValue === undefined) {
            slow = this.alphaSlow * currentPrice + (1 - this.alphaSlow) * prevPrice;
        }
        else {
            slow = this.alphaSlow * currentPrice + (1 - this.alphaSlow) * this.slowEMAValue;
        }
        const differ = fast - slow;
        if (saveState) {
            this.fastEMAValue = fast;
            this.slowEMAValue = slow;
            this.macdHistory.push(differ);
        }
        if (!updateLines) {
            return;
        }
        this.SetValue(0, 0, differ);
        if (this.FastEMA + this.SignalPeriod < this.CurrentData.Count && this.macdHistory.length + 1 >= this.SignalPeriod) {
            const recent = this.SignalPeriod === 1 ? [differ] : this.macdHistory.slice(-(this.SignalPeriod - 1)).concat(differ);
            const sumSignal = recent.reduce((acc, v) => acc + v, 0);
            const signal = sumSignal / recent.length;
            this.SetValue(1, 0, signal);
            this.SetValue(2, 0, differ - signal);
        }
    }
    NextBar() {
        super.NextBar();
        this.UpdateMACD(true, 1, false);
    }
    OnQuote() {
        super.OnQuote();
        this.UpdateMACD(false);
    }
}
